import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, DocumentArrowDownIcon, ExclamationTriangleIcon, InformationCircleIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { saveAs } from 'file-saver';
import { Modal } from 'flowbite-react';
import JSZip from 'jszip';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { BlogPostStatusEnum } from '../../../@types/BlogPost';
import type { Company, ReferentMutation } from '../../../@types/Company';
import type { RootState } from '../../../store/store';
import { getValidReferents, isCompanyBrandingComplete } from '../../../utils/branding';
import { JWTHeader } from '../../../utils/JWTHeader';
import { buildSuggestedMailText, buildZipFilenameFromTitle } from '../../../utils/newsletterMail';
import { renderNewsletterPDFsForReferents, renderNewsletterToPDFBlob } from '../../../utils/newsletterPDF';
import { generateQRCodeUrl, processFixedNewsletterContent, processFixedNewsletterContentForReferent } from '../../../utils/newsletterPreview';
import { ROUTES } from '../../../utils/routes';
import { showToast } from '../../common/Notification';
import CompanyBrandingEdition from '../components/CompanyBrandingEdition/CompanyBrandingEdition.module';

/**
 * Helper to sanitize a referent name for use in filenames
 */
const sanitizeReferentName = (referent: ReferentMutation): string => {
  return `${referent.firstname}-${referent.lastname}`
    .toLowerCase()
    .replaceAll(/[^a-z0-9-]/g, '-')
    .replaceAll(/-+/g, '-');
};

/**
 * Helper to add QR code PNG images for referents with external newsletter URLs
 * These images are added to the ZIP for reference when the blog link is replaced
 */
const addExternalNewsletterQRCodes = async(
  zip: JSZip,
  referents: ReferentMutation[],
): Promise<void> => {
  const referentsWithExternalUrls = referents.filter(
    r => r.hasExternalNewsletter && r.externalNewsletterUrl,
  );
  
  if (referentsWithExternalUrls.length === 0) {
    return;
  }

  // Create a folder for QR codes if there are any
  const qrFolder = zip.folder('QR-Codes-URL-Personnalisees');
  if (!qrFolder) {return;}

  // Fetch and add each QR code PNG in parallel
  const qrPromises = referentsWithExternalUrls.map(async(referent) => {
    try {
      const qrCodeUrl = generateQRCodeUrl('', referent.externalNewsletterUrl);
      const response = await fetch(qrCodeUrl);
      
      if (response.ok) {
        const blob = await response.blob();
        const sanitizedName = sanitizeReferentName(referent);
        // Create a descriptive filename: QR-referent-name-url.png
        const filename = `QR-${sanitizedName}.png`;
        qrFolder.file(filename, blob);
        
        // Also add a text file with the URL for reference
        const urlFilename = `URL-${sanitizedName}.txt`;
        qrFolder.file(urlFilename, `URL personnalisée pour ${referent.firstname} ${referent.lastname}:\n${referent.externalNewsletterUrl}`);
      }
    } catch (error) {
      console.warn(`Failed to fetch QR code for referent ${referent.firstname} ${referent.lastname}:`, error);
    }
  });

  await Promise.all(qrPromises);
};

interface NewsletterForPDF {
  id: string;
  subject: string;
  content: string;
  status: string;
  type: string;
  createdAt: string;
}

interface FixedNewsletterData {
  date: string;
  slug: string;
  templateName: string;
  title: string;
  content: string;
  pageCount: number;
}

// Mock Newsletter interface for PDF export compatibility
interface NewsletterForPDF {
  id: string;
  subject: string;
  content: string;
  status: string;
  type: string;
  createdAt: string;
}

function NewsletterPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company) as Company;

  const [newsletterData, setNewsletterData] = useState<FixedNewsletterData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processedContent, setProcessedContent] = useState<string>('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [hasBeenSent, setHasBeenSent] = useState<boolean | null>(null);
  const [showBrandingEditor, setShowBrandingEditor] = useState(false);
  const [isProcessingZip, setIsProcessingZip] = useState(false);

  const isBrandingComplete = React.useMemo(() => isCompanyBrandingComplete(company), [company]);
  
  // Get all valid referents for multi-PDF generation
  const validReferents = useMemo(() => getValidReferents(company), [company]);

  // Selected referent index for preview (cycle through referents)
  const [selectedReferentIndex, setSelectedReferentIndex] = useState(0);
  const selectedReferent = validReferents[selectedReferentIndex] ?? validReferents[0] ?? null;
  const hasMultipleReferents = validReferents.length > 1;

  // Keep index in bounds when referents change
  useEffect(() => {
    if (selectedReferentIndex >= validReferents.length) {
      setSelectedReferentIndex(Math.max(0, validReferents.length - 1));
    }
  }, [validReferents.length, selectedReferentIndex]);

  const handlePreviousReferent = useCallback(() => {
    setSelectedReferentIndex((prev) =>
      prev === 0 ? validReferents.length - 1 : prev - 1,
    );
  }, [validReferents.length]);

  const handleNextReferent = useCallback(() => {
    setSelectedReferentIndex((prev) =>
      prev === validReferents.length - 1 ? 0 : prev + 1,
    );
  }, [validReferents.length]);

  const checkIfSent = async(date: string, slug: string, templateName: string) => {
    if (!company || !jwt) {return;}

    try {
      const response = await fetch(
        `/api/companies/${company.id}/fixed-newsletters/${date}/${slug}/templates/${templateName}/sent`,
        JWTHeader(jwt),
      );

      if (response.ok) {
        const data = await response.json();
        setHasBeenSent(data.sent);
      }
    } catch (error) {
      console.error('Error checking sent status:', error);
    }
  };

  useEffect(() => {
    if (!id || !jwt) {
      navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
      return;
    }

    const loadFixedNewsletter = async() => {
      try {
        // Parse the fixed newsletter ID: "fixed:date:slug:templateName"
        const parts = id.split(':');
        if (parts.length !== 4 || parts[0] !== 'fixed') {
          throw new Error('Invalid fixed newsletter ID format');
        }

        const [, date, slug, templateName] = parts;

        // Fetch the newsletter data
        const response = await fetch(
          `/api/fixed-newsletters/${date}/${slug}`,
          JWTHeader(jwt),
        );

        if (!response.ok) {
          throw new Error('Failed to fetch newsletter');
        }

        const data = await response.json();

        // Find the specific template
        const template = data.templates.find((t: { name: string; content: string; pageCount?: number }) => t.name === templateName);
        if (!template) {
          throw new Error('Template not found');
        }

        setNewsletterData({
          date,
          slug,
          templateName,
          title: data.title,
          content: template.content,
          pageCount: template.pageCount || 0,
        });
        
        // Check if this newsletter has been sent after loading the data
        if (company) {
          await checkIfSent(date, slug, templateName);
        }
      } catch (error) {
        console.error('Error loading fixed newsletter:', error);
        showToast('error', 'Erreur lors du chargement de la newsletter');
        navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
      } finally {
        setIsLoading(false);
      }
    };

    loadFixedNewsletter();
  }, [id, jwt, navigate, company]); // Include company since we now check sent status in this effect

  // Process the newsletter content with company branding for the selected referent
  useEffect(() => {
    const processContent = async() => {
      if (!newsletterData || !company) {return;}

      try {
        // When there are multiple referents, process content for the selected referent
        // so the preview shows the correct referent's data
        let processed: string;
        if (selectedReferent) {
          processed = await processFixedNewsletterContentForReferent(
            newsletterData.content,
            company,
            selectedReferent,
            jwt,
          );
        } else {
          processed = await processFixedNewsletterContent(
            newsletterData.content,
            company,
            jwt,
          );
        }
        setProcessedContent(processed);
      } catch (error) {
        console.error('Error processing newsletter content:', error);
        showToast('error', 'Une erreur est survenue lors du traitement du contenu de la newsletter.');
      }
    };

    processContent();
  }, [
    newsletterData,
    jwt,
    selectedReferent,
    company?.id,
    company?.brandingSettings?.color1,
    company?.brandingSettings?.color2,
    company?.brandingSettings?.color3,
    company?.brandingSettings?.referents,
    company?.brandingSettings?.brandingImageName,
  ]);

  // Update resize observer effect for responsive scaling
  useEffect(() => {
    const newsletterContainer = document.querySelector('#newsletter-content') as HTMLElement;
    
    if (!newsletterContainer) {
      console.warn('Newsletter scaling: Container #newsletter-content not found');
      return;
    }

    // Find the actual container that changes size with window resize
    // This should be the flex container that wraps the newsletter content
    const flexContainer = newsletterContainer.closest('.flex') as HTMLElement;
    const observableContainer = flexContainer || newsletterContainer.parentElement as HTMLElement;

    // The content element is the one with the transform: scale style applied
    // Look for the element with width: 210mm in its inline styles
    const content = newsletterContainer.querySelector('div[style*="width: 210mm"]') as HTMLElement | null;
    
    if (!content) {
      console.warn('Newsletter scaling: Content element with width: 210mm not found');
      // Fallback: try to find any element with transform: scale in its style
      const fallbackContent = newsletterContainer.querySelector('div[style*="transform: scale"]') as HTMLElement | null;
      if (!fallbackContent) {
        console.error('Newsletter scaling: No suitable content element found');
        return;
      }
    }

    const targetElement = content || newsletterContainer.querySelector('div[style*="transform: scale"]') as HTMLElement;
    
    if (!targetElement) {
      console.error('Newsletter scaling: No target element found for scaling');
      return;
    }

    
    const updateScale = () => {
      // Get the actual available width - use the observable container's width minus padding
      const containerWidth = observableContainer ? observableContainer.clientWidth - 32 : newsletterContainer.clientWidth; // Account for padding
      // 210mm = 793.7007874016 pixels at 96 DPI (more precise calculation)
      const contentWidth = (210 / 25.4) * 96; // mm to inches to pixels
      const scaleFactor = Math.min(1, containerWidth / contentWidth);
      
      
      // Apply the scale factor
      targetElement.style.setProperty('--scale-factor', scaleFactor.toString());
    };
    
    const resizeObserver = new ResizeObserver(() => {
      updateScale();
    });

    // Observe the container that actually changes size with window resize
    if (observableContainer) {
      resizeObserver.observe(observableContainer);
    }
    
    // Also listen to window resize as a fallback
    const handleWindowResize = () => {
      setTimeout(updateScale, 10); // Small delay to let layout settle
    };
    
    window.addEventListener('resize', handleWindowResize);
    
    // Trigger initial calculation
    updateScale();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [processedContent]);

  const handleSendNewsletter = async() => {
    setIsConfirmModalOpen(true);
  };

  // Helper function to manage scale factor for PDF export
  const setContentScale = (scale: number, compensateWrapper: boolean = false) => {
    // Use the same selector logic as the ResizeObserver
    const container = document.querySelector('#newsletter-content') as HTMLElement;
    const content = container?.querySelector('div[style*="width: 210mm"]') as HTMLElement | null ||
                   container?.querySelector('div[style*="transform: scale"]') as HTMLElement | null;
    
    if (content) {
      content.style.setProperty('--scale-factor', scale.toString());
      if (compensateWrapper) {
        // Store original styles before modifying
        const originalStyles = {
          width: content.style.width,
          transform: content.style.transform,
          transformOrigin: content.style.transformOrigin,
          margin: content.style.margin,
        };
        
        // Apply PDF export styles
        content.style.width = `${210 * 3.78 * (1 / scale)}px`;
        content.style.transform = 'none';
        content.style.transformOrigin = 'initial';
        content.style.margin = '0';
        
        return originalStyles;
      }
      
      // Reset to default styles for A4
      content.style.width = '210mm';
      content.style.transform = `scale(var(--scale-factor, 1))`;
      content.style.transformOrigin = 'top left';
      content.style.margin = '0 auto';
    }
    return null;
  };

  // Helper to prepare newsletter data for a specific referent (content processing only)
  const prepareNewsletterForReferent = async(referent: ReferentMutation): Promise<{ newsletter: NewsletterForPDF; referent: ReferentMutation }> => {
    const referentContent = await processFixedNewsletterContentForReferent(
      newsletterData!.content,
      company,
      referent,
      jwt,
    );
    
    return {
      newsletter: {
        id: `fixed:${newsletterData!.date}:${newsletterData!.slug}:${newsletterData!.templateName}`,
        subject: newsletterData!.title,
        content: referentContent,
        status: BlogPostStatusEnum.PUBLIC,
        type: 'fixed',
        createdAt: new Date().toISOString(),
      },
      referent,
    };
  };

  const handleConfirmSend = async() => {
    try {
      if (!newsletterData || !company || !jwt) {
        throw new Error('Missing required data');
      }

      // Store current scale factor and styles
      const container = document.querySelector('#newsletter-content') as HTMLElement;
      const content = container?.querySelector('div[style*="width: 210mm"]') as HTMLElement | null ||
                     container?.querySelector('div[style*="transform: scale"]') as HTMLElement | null;
      const currentScale = content?.style.getPropertyValue('--scale-factor') || '1';
      
      // Reset scale to 1 for PDF export and compensate wrapper, store original styles
      const originalStyles = setContentScale(1, true);

      setIsProcessingZip(true);

      // Prepare Mail.txt content
      const mailText = buildSuggestedMailText(company, newsletterData.title);

      // Fetch Désinscriptions.csv content
      let unsubCsv = 'Email,Date de désinscription\n';
      try {
        const csvResponse = await fetch(
          `/api/companies/${company.id}/unsubscribers/export/csv`,
          JWTHeader(jwt),
        );
        if (csvResponse.ok) {
          unsubCsv = await csvResponse.text();
        }
      } catch {
        console.warn('Unable to fetch Désinscriptions.csv, using empty CSV header.');
      }

      // Assemble ZIP
      const zip = new JSZip();
      zip.file('Mail.txt', mailText);
      zip.file('Désinscriptions.csv', unsubCsv);
      
      // Generate PDFs for each referent (multi-referent system)
      if (validReferents.length > 1) {
        // Multiple referents: create a PDF per referent
        const pdfFolder = zip.folder('Newsletters');
        // Content processing can be parallelised (no DOM dependency)
        const preparedNewsletters = await Promise.all(
          validReferents.map(referent => prepareNewsletterForReferent(referent)),
        );
        // PDF rendering must be sequential — html2canvas captures global DOM state
        // and concurrent calls corrupt each other's output
        const pdfResults = await renderNewsletterPDFsForReferents(preparedNewsletters);
        for (const { blob: pdfBlob, filename: pdfFilename } of pdfResults) {
          pdfFolder?.file(pdfFilename, pdfBlob);
        }
        showToast('info', `${pdfResults.length} PDFs générés pour ${validReferents.length} référents.`);
      } else {
        // Single referent or legacy: use the current processed content
        const newsletterForPDF: NewsletterForPDF = {
          id: `fixed:${newsletterData.date}:${newsletterData.slug}:${newsletterData.templateName}`,
          subject: newsletterData.title,
          content: processedContent,
          status: BlogPostStatusEnum.PUBLIC,
          type: 'fixed',
          createdAt: new Date().toISOString(),
        };
        const { blob: pdfBlob } = await renderNewsletterToPDFBlob(newsletterForPDF);
        zip.file('Newsletter.pdf', pdfBlob);
      }
      
      // Add QR code PNG images for referents with custom external newsletter URLs
      await addExternalNewsletterQRCodes(zip, validReferents);
      
      const safeZipName = buildZipFilenameFromTitle(newsletterData.title);
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${safeZipName}.zip`);

      // Restore previous scale and original styles
      if (content && originalStyles) {
        Object.assign(content.style, originalStyles);
        content.style.setProperty('--scale-factor', currentScale);
      }

      // Then mark as sent via API
      const response = await fetch(
        `/api/companies/${company.id}/fixed-newsletters/${newsletterData.date}/${newsletterData.slug}/templates/${newsletterData.templateName}/send`,
        {
          ...JWTHeader(jwt),
          method: 'POST',
        },
      );

      if (!response.ok) {
        throw new Error('Failed to mark newsletter as sent');
      }

      setHasBeenSent(true);
      const pdfCount = validReferents.length > 1 ? `${validReferents.length} PDFs` : 'PDF';
      showToast('success', `ZIP téléchargé (Mail, Désinscriptions, ${pdfCount}) et newsletter marquée comme envoyée.`);
    } catch (error) {
      console.error('Error handling newsletter send:', error);
      showToast('error', 'Une erreur est survenue lors de la génération du ZIP ou de l\'envoi.');
    } finally {
      setIsProcessingZip(false);
      setIsConfirmModalOpen(false);
    }
  };

  const handleDownloadPDF = async() => {
    try {
      if (!newsletterData || !company) {
        throw new Error('Missing required data');
      }

      // Store current scale factor and styles
      const container = document.querySelector('#newsletter-content') as HTMLElement;
      const content = container?.querySelector('div[style*="width: 210mm"]') as HTMLElement | null ||
                     container?.querySelector('div[style*="transform: scale"]') as HTMLElement | null;
      const currentScale = content?.style.getPropertyValue('--scale-factor') || '1';
      
      // Reset scale to 1 for PDF export and compensate wrapper, store original styles
      const originalStyles = setContentScale(1, true);

      const mailText = buildSuggestedMailText(company, newsletterData.title);
      let unsubCsv = 'Email,Date de désinscription\n';
      try {
        const csvResponse = await fetch(
          `/api/companies/${company.id}/unsubscribers/export/csv`,
          JWTHeader(jwt),
        );
        if (csvResponse.ok) {
          unsubCsv = await csvResponse.text();
        }
      } catch {
        console.warn('Unable to fetch Désinscriptions.csv, using empty CSV header.');
      }

      const zip = new JSZip();
      zip.file('Mail.txt', mailText);
      zip.file('Désinscriptions.csv', unsubCsv);
      
      // Generate PDFs for each referent (multi-referent system)
      if (validReferents.length > 1) {
        // Multiple referents: create a PDF per referent
        const pdfFolder = zip.folder('Newsletters');
        // Content processing can be parallelised (no DOM dependency)
        const preparedNewsletters = await Promise.all(
          validReferents.map(referent => prepareNewsletterForReferent(referent)),
        );
        // PDF rendering must be sequential — html2canvas captures global DOM state
        // and concurrent calls corrupt each other's output
        const pdfResults = await renderNewsletterPDFsForReferents(preparedNewsletters);
        for (const { blob: pdfBlob, filename: pdfFilename } of pdfResults) {
          pdfFolder?.file(pdfFilename, pdfBlob);
        }
      } else {
        // Single referent or legacy: use the current processed content
        const newsletterForPDF: NewsletterForPDF = {
          id: `fixed:${newsletterData.date}:${newsletterData.slug}:${newsletterData.templateName}`,
          subject: newsletterData.title,
          content: processedContent,
          status: BlogPostStatusEnum.PUBLIC,
          type: 'fixed',
          createdAt: new Date().toISOString(),
        };
        const { blob: pdfBlob } = await renderNewsletterToPDFBlob(newsletterForPDF);
        zip.file('Newsletter.pdf', pdfBlob);
      }
      
      // Add QR code PNG images for referents with custom external newsletter URLs
      await addExternalNewsletterQRCodes(zip, validReferents);
      
      const safeZipName = buildZipFilenameFromTitle(newsletterData.title);
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${safeZipName}.zip`);
      const pdfCount = validReferents.length > 1 ? `${validReferents.length} PDFs` : 'PDF';
      showToast('success', `ZIP téléchargé (Mail, Désinscriptions, ${pdfCount}).`);

      // Restore previous scale and original styles
      if (content && originalStyles) {
        Object.assign(content.style, originalStyles);
        content.style.setProperty('--scale-factor', currentScale);
      }
    } catch (error) {
      console.error('Error downloading ZIP:', error);
      showToast('error', 'Une erreur est survenue lors de la génération du ZIP.');
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div className="animate-pulse">
          <div className="mb-4 h-8 w-1/3 rounded bg-gray-200"></div>
          <div className="mb-6 h-4 w-1/2 rounded bg-gray-200"></div>
          <div className="h-96 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  if (!newsletterData) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-md">
        <p className="text-red-600">Newsletter non trouvée</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 rounded-lg bg-white p-4 shadow-md">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST)}
              className="mr-4 flex items-center text-rgpd-blue"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <h1 className="grow text-2xl font-bold">Prévisualisation de la newsletter</h1>
          </div>

          {/* Suggested Mail Content Section */}
          <div className="border-t pt-6">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-700">Contenu du mail de Newsletter suggéré et RGPD compatible</h2>
              <div className="flex gap-2">
                <button
                  onClick={async() => {
                    try {
                      const text = buildSuggestedMailText(company, newsletterData.title, newsletterData.date);
                      await navigator.clipboard.writeText(text);
                      showToast('success', 'Contenu du mail copié dans le presse-papiers.');
                    } catch {
                      showToast('error', 'Impossible de copier le contenu du mail.');
                    }
                  }}
                  className="rounded-lg bg-rgpd-blue px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-dark"
                >
                  Copier le contenu
                </button>
              </div>
            </div>
            <div className="mb-6">
              <textarea
                className="w-full rounded-lg border p-3 text-sm text-gray-800"
                rows={8}
                readOnly
                value={buildSuggestedMailText(company, newsletterData.title)}
              />
            </div>
          </div>

          {/* Company Branding Edition Section */}
          <div className="border-t pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-700">Personnalisation en temps réel</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowBrandingEditor(!showBrandingEditor)}
                  className="rounded-lg bg-rgpd-blue px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-dark"
                >
                  {showBrandingEditor ? 'Masquer l\'édition de l\'image de l\'entreprise' : 'Modifier l\'image de l\'entreprise'}
                </button>
              </div>
            </div>
            
            {showBrandingEditor && (
              <div className="mb-6">
                <CompanyBrandingEdition showBlogStructure={false} />
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50">
            <div className="mb-4 rounded-lg bg-white p-4 shadow-md">
              <div className="items-center justify-between gap-4 md:flex">
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-800">
                    Objet : {newsletterData.title}
                  </h1>
                </div>
                <div className="hidden h-12 w-px bg-gray-200 md:block" />
                <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
                  {hasBeenSent === true ? (
                    <button 
                      className="flex w-full items-center justify-center rounded-xl border border-rgpd-blue px-4 py-2 text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-rgpd-white md:w-auto"
                      onClick={handleDownloadPDF}
                    >
                      <span>Télécharger de nouveau</span>
                      <DocumentArrowDownIcon className="ml-2 inline h-6 w-6" />
                    </button>
                  ) : (
                    <button 
                      className="flex w-full items-center justify-center rounded-xl border border-rgpd-blue px-4 py-2 text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-rgpd-white md:w-auto"
                      onClick={handleSendNewsletter}
                    >
                      <span>Enregistrer la Newsletter en PDF et enregistrer comme envoyée</span>
                      <PaperAirplaneIcon className="ml-2 inline h-6 w-6" />
                    </button>
                  )}
                  <button
                    className="flex w-full items-center justify-center rounded-xl border border-rgpd-blue px-4 py-2 text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-rgpd-white md:w-auto"
                    onClick={() => navigate(ROUTES.COMMUNICATION_UNSUBSCRIBERS)}
                  >
                    Gérer les désinscriptions
                  </button>
                </div>
              </div>
            </div>

            {/* Referent selector for multi-referent preview */}
            {hasMultipleReferents && selectedReferent && (
              <div className="mx-4 mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">
                      Prévisualisation pour le référent :
                    </span>
                    <span className="rounded-md bg-white px-3 py-1 text-sm font-semibold text-rgpd-blue shadow-sm">
                      {selectedReferent.title} {selectedReferent.firstname} {selectedReferent.lastname}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({selectedReferentIndex + 1}/{validReferents.length})
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handlePreviousReferent}
                      className="rounded-lg p-1.5 text-gray-600 transition-colors hover:bg-blue-100 hover:text-rgpd-blue"
                      title="Référent précédent"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleNextReferent}
                      className="rounded-lg p-1.5 text-gray-600 transition-colors hover:bg-blue-100 hover:text-rgpd-blue"
                      title="Référent suivant"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {validReferents.length} référents configurés — le ZIP contiendra un PDF par référent.
                </p>
              </div>
            )}

            {/* Newsletter content layout */}
            <div className="mx-auto max-w-7xl px-4 pb-12">
              <div className="flex gap-6">
                {/* Newsletter Content */}
                <div className="flex-1" id="newsletter-content">
                  <div className="relative">
                    {/* A4 Format Container */}
                    <div className="flex justify-center">
                      <div className="w-full" style={{ maxWidth: '210mm' }}>
                        <div 
                          className="origin-top bg-white shadow-md"
                          style={{
                            width: '210mm',
                            minHeight: '297mm',
                            transform: 'scale(var(--scale-factor, 1))',
                            transformOrigin: 'top left',
                            margin: '0 auto',
                          }}
                        >
                          <div
                            className="newsletter-content"
                            style={{
                              minHeight: '100%',
                              boxSizing: 'border-box',
                            }}
                            dangerouslySetInnerHTML={{ __html: processedContent }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Overlay when branding is not complete */}
                    {!isBrandingComplete && (
                      <div className="absolute inset-0 flex items-start justify-center rounded-lg bg-black/50 pt-12">
                        <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
                          <InformationCircleIcon className="mx-auto mb-4 h-16 w-16 text-yellow-500" />
                          <h3 className="mb-4 text-xl font-semibold">Personnalisation de l'entreprise requise</h3>
                          <p className="mb-6 text-left text-gray-600">
                            Vous devez compléter la personnalisation de votre entreprise pour pouvoir consulter vos newsletters. <br /><br />
                            Il vous faut : les couleurs, le logo, les informations du référent et sa photo.
                          </p>
                          <button
                            onClick={() => setShowBrandingEditor(true)}
                            className="rounded-lg bg-rgpd-blue px-6 py-3 text-white transition-colors hover:bg-rgpd-blue-dark"
                          >
                            Compléter la personnalisation
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Side Panel for Color Editing - Only visible on large screens */}
                <div className="hidden w-80 shrink-0 xl:block">
                  <div className="sticky top-6">
                    <CompanyBrandingEdition colorOnlyMode={true} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal show={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} className="z-[1000]">
        <Modal.Header>Confirmer l'envoi de la newsletter</Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            <div className="space-y-3 text-sm text-gray-700">
              <p className="text-gray-600">
                Cette action va télécharger un fichier ZIP contenant :
              </p>
              <ul className="list-disc space-y-1 pl-6">
                <li><b>Mail.txt</b>: contenu du courriel prêt à copier/coller (RGPD compatible).</li>
                <li><b>Désinscriptions.csv</b>: la liste des adresses désinscrites à ne pas contacter.</li>
                {hasMultipleReferents ? (
                  <li><b>Newsletters/</b>: un dossier contenant {validReferents.length} PDFs (un par référent).</li>
                ) : (
                  <li><b>Newsletter.pdf</b>: la newsletter en PDF.</li>
                )}
              </ul>
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 text-red-600" />
                <p className="text-red-700">
                  Ne jamais envoyer d'e-mail aux adresses présentes dans <b>Désinscriptions.csv</b>.
                </p>
              </div>
              <p className="text-gray-600">La newsletter sera également marquée comme envoyée.</p>
            </div>
            <div className="flex items-center justify-end space-x-2">
              <button
                onClick={() => setIsConfirmModalOpen(false)}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmSend}
                className="rounded-lg bg-rgpd-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rgpd-blue-dark disabled:opacity-60"
                disabled={isProcessingZip}
              >
                Télécharger le ZIP et marquer comme envoyée
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default NewsletterPreview; 